import { api } from '@/lib/api';
import { UploadAttachmentResponseSchema, type UploadAttachmentResponse } from './schemas';
import { z } from 'zod';

function tenantHeaders(tenantId: string): Record<string, string> {
  return { 'X-Tenant-Id': tenantId };
}

/** Multipart upload. Skips the central `api.post` JSON helper. */
export async function uploadAttachment(
  tenantId: string,
  file: File,
  options?: { embedSync?: boolean },
): Promise<UploadAttachmentResponse> {
  const form = new FormData();
  form.append('file', file);
  if (options?.embedSync) form.append('embed_sync', 'true');
  const res = await fetch('/api/tenant/attachments', {
    method: 'POST',
    credentials: 'same-origin',
    headers: tenantHeaders(tenantId),
    body: form,
  });
  if (!res.ok) {
    let message = `Upload failed: ${res.status}`;
    try {
      const envelope = (await res.json()) as { error?: { message?: string } };
      if (envelope.error?.message) message = envelope.error.message;
    } catch {
      /* ignore */
    }
    throw new Error(message);
  }
  const raw = (await res.json()) as unknown;
  return UploadAttachmentResponseSchema.parse(raw);
}

export async function deleteAttachment(tenantId: string, id: string): Promise<void> {
  await api.delete(`/tenant/attachments/${encodeURIComponent(id)}`, z.void(), {
    headers: tenantHeaders(tenantId),
  });
}

/**
 * Anchor-href URL for downloading an attachment. Browser navigations can't
 * set X-Tenant-Id, so tenant id rides as the `_t` query param (the tenant
 * middleware accepts it as a fallback — same pattern as chat-stream).
 */
export function attachmentDownloadUrl(tenantId: string, id: string): string {
  const params = new URLSearchParams({ _t: tenantId });
  return `/api/tenant/attachments/${encodeURIComponent(id)}?${params}`;
}

/**
 * Same as attachmentDownloadUrl but with `?inline=1` so the browser renders
 * the file inline (Content-Disposition: inline) instead of forcing a download.
 * Use for HTML/PDF/image attachments that should open in a new tab or inside
 * an embedded viewer rather than downloading.
 */
export function attachmentInlineUrl(tenantId: string, id: string): string {
  const params = new URLSearchParams({ _t: tenantId, inline: '1' });
  return `/api/tenant/attachments/${encodeURIComponent(id)}?${params}`;
}
